import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { trackEvent } from '../utils/analytics';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { API_BASE_URL, authFetch } from '../config/api';

interface SearchResult {
  id: number;
  file_path: string;
  title: string;
  section_heading: string | null;
  content_chunk: string;
  similarity: number;
  source_type: string;
}

const baseUrl = API_BASE_URL;

async function fetchSearch(query: string): Promise<SearchResult[]> {
  const res = await authFetch(
    `${baseUrl}/api/knowledge/search?q=${encodeURIComponent(query)}&limit=30`,
  );
  if (!res.ok) throw new Error(await res.text());
  const data = (await res.json()) as { ok: boolean; results: SearchResult[] };
  if (!data.ok) throw new Error('Search failed');
  return data.results;
}

function obsidianLink(filePath: string): string {
  const file = filePath.replace(/\.md$/, '');
  return `obsidian://open?vault=Obsidian&file=${encodeURIComponent(file)}`;
}

/** Group results by note title so multiple chunks from the same note appear together. */
function groupByTitle(results: SearchResult[]): Map<string, SearchResult[]> {
  const map = new Map<string, SearchResult[]>();
  for (const r of results) {
    const key = r.file_path;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(r);
  }
  return map;
}

export default function SearchResultsPage() {
  const [params] = useSearchParams();
  const q = params.get('q') ?? '';

  const { data: results, isLoading, isError, error } = useQuery<SearchResult[]>(
    ['searchResults', q],
    () => fetchSearch(q),
    { enabled: q.length >= 2 },
  );

  useEffect(() => {
    if (q.length >= 2 && results) {
      trackEvent('Search', { query: q, results: String(results.length) });
    }
  }, [q, results]);

  const grouped = results ? groupByTitle(results) : new Map();

  return (
    <div className="search-results-page">
      <h2 className="search-results-page__title">
        Search results for &ldquo;{q}&rdquo;
      </h2>

      {isLoading && <div className="loading-state">Searching...</div>}
      {isError && <div className="error-state">Error: {(error as Error).message}</div>}

      {results && results.length === 0 && (
        <div className="search-results-page__empty">
          No results found. Try a different search term.
        </div>
      )}

      {[...grouped.entries()].map(([filePath, chunks]) => (
        <div key={filePath} className="search-result-group">
          <div className="search-result-group__header">
            <a
              className="search-result-group__title"
              href={obsidianLink(filePath)}
              title="Open in Obsidian"
            >
              {chunks[0].title}
            </a>
            <span className="search-result-group__path">{filePath}</span>
          </div>
          {chunks.map((r) => (
            <div key={r.id} className="search-result-card">
              <div className="search-result-card__header">
                {r.section_heading && (
                  <span className="search-result-card__heading">{r.section_heading}</span>
                )}
                <span className="similarity-badge">
                  {Math.round(r.similarity * 100)}%
                </span>
              </div>
              <div className="search-result-card__content search-result-card__content--markdown">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{r.content_chunk}</ReactMarkdown>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
